import React, { useState, useMemo, useEffect } from 'react';
import {
  Group,
  Text,
  Badge,
  ActionIcon,
  Checkbox,
  Modal,
  TextInput,
  Stack,
  Menu,
  Button,
  Box,
  Tooltip,
} from '@mantine/core';
import {
  IconEdit,
  IconTrash,
  IconCopy,
  IconDots,
  IconCalendar,
  IconCircleCheck,
  IconLayoutList,
  IconList,
} from '@tabler/icons-react';
import { DatabaseItem, Database } from '../../../types/models/Database';

interface ListViewProps {
  database: Database;
  items: DatabaseItem[];
  visibleProperties?: string[];
  onItemSelect?: (item: DatabaseItem) => void;
  onItemCreate?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  onItemDelete?: (itemId: string) => void;
  onItemDuplicate?: (item: DatabaseItem) => void;
}

// F 85 — G-1: prop type now exported as part of the public view API.
export type { ListViewProps };

// 12 deterministic gradient covers so every list card has visual variety.
// The actual values are in coverResolver.ts; this is a fallback only.
import { resolveCover } from '../database/coverResolver';

const hashId = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
};

const getCover = (item: DatabaseItem, database?: Database) => resolveCover(item, database);

export const ListView: React.FC<ListViewProps> = ({
  database,
  items,
  visibleProperties = [],
  onItemSelect,
  onItemUpdate,
  onItemDelete,
  onItemDuplicate,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [editingItem, setEditingItem] = useState<DatabaseItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // Density: 'comfortable' = spacious rows, 'compact' = dense rows.
  // Persisted per-user in localStorage.
  const [density, setDensity] = useState<'comfortable' | 'compact'>(() => {
    if (typeof window === 'undefined') return 'comfortable';
    return (localStorage.getItem('freshcards:listDensity') as 'comfortable' | 'compact') || 'comfortable';
  });
  useEffect(() => {
    localStorage.setItem('freshcards:listDensity', density);
  }, [density]);
  // Mobile vs desktop detection
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Properties to show as columns (desktop) or stacked chips (mobile)
  const displayProperties = useMemo(() => {
    const propsToShow = visibleProperties.length > 0
      ? database.schema.properties.filter(p => visibleProperties.includes(p.id))
      : database.schema.properties.slice(0, 4);
    return propsToShow;
  }, [database.schema.properties, visibleProperties]);

  const getPropertyValue = (item: DatabaseItem, propId: string): any => {
    if (propId === 'title') return item.title;
    const prop = item.properties.find(p => p.propertyId === propId);
    return prop?.value ?? '-';
  };

  const renderStatusPill = (value: any, prop: any) => {
    if (!value) return null;
    if (typeof value === 'object' && value.name) {
      return (
        <Badge
          size="sm"
          variant="light"
          style={{ backgroundColor: value.color ? `${value.color}22` : undefined, color: value.color || undefined, border: 'none' }}
        >
          {value.name}
        </Badge>
      );
    }
    const opt = prop?.options?.find((o: any) => o.id === value);
    if (opt) {
      return <Badge size="sm" variant="light" color="blue">{opt.name}</Badge>;
    }
    return <Badge size="sm" variant="light">{String(value)}</Badge>;
  };

  const handleDeleteItem = (itemId: string) => {
    if (confirm('Delete this item?')) onItemDelete?.(itemId);
  };

  const handleSelectAll = () => {
    setSelectedItems(prev => prev.length === items.length ? [] : items.map(i => i.id));
  };

  const toggleSelect = (id: string) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // ─── MOBILE: rich card list ─────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="list-view-wrapper" style={{ width: '100%' }}>
        {/* Bulk action bar — shown when items are selected */}
        {selectedItems.length > 0 && (
          <Group
            justify="space-between"
            align="center"
            px="md"
            py="xs"
            style={{
              position: 'sticky', top: 0, zIndex: 10,
              backgroundColor: 'var(--fc-accent-primary)',
              color: 'white',
            }}
          >
            <Text size="sm" fw={500}>{selectedItems.length} selected</Text>
            <Group gap="xs">
              <ActionIcon variant="subtle" color="red" onClick={() => {
                if (confirm(`Delete ${selectedItems.length} items?`)) {
                  selectedItems.forEach(id => onItemDelete?.(id));
                  setSelectedItems([]);
                }
              }}><IconTrash size={16} /></ActionIcon>
              <ActionIcon variant="subtle" onClick={() => setSelectedItems([])}>
                <IconCircleCheck size={16} />
              </ActionIcon>
            </Group>
          </Group>
        )}

        <Box p="xs" className="fc-list-cards">
          {items.length === 0 ? (
            <Box py="xl" ta="center">
              <Text c="dimmed">No items in this view</Text>
            </Box>
          ) : (
            items.map(item => {
              const isSelected = selectedItems.includes(item.id);
              const statusProp = displayProperties.find((p: any) => p.id === 'status' || p.type === 'status');
              const priorityProp = displayProperties.find((p: any) => p.id === 'priority' || p.type === 'priority');
              const dateProp = displayProperties.find((p: any) => p.id === 'date' || p.type === 'date');
              const tagProp = displayProperties.find((p: any) => p.id === 'tag' || p.type === 'multi_select');
              return (
                <Box
                  key={item.id}
                  className={`fc-list-card ${isSelected ? 'fc-list-card-selected' : ''} ${density === 'compact' ? 'fc-list-card--compact' : ''}`}
                  onClick={() => onItemSelect?.(item)}
                >
                  {/* Cover thumbnail */}
                  <div
                    className="fc-list-card-cover"
                    style={{ background: getCover(item, database) }}
                    aria-hidden="true"
                  />
                  {/* Main content */}
                  <div className="fc-list-card-body">
                    <Text size="sm" fw={600} lineClamp={1} mb={2}>
                      {item.title}
                    </Text>
                    <div className="fc-list-card-meta">
                      {statusProp && renderStatusPill(getPropertyValue(item, statusProp.id), statusProp)}
                      {priorityProp && renderStatusPill(getPropertyValue(item, priorityProp.id), priorityProp)}
                      {dateProp && getPropertyValue(item, dateProp.id) !== '-' && (
                        <span className="fc-list-card-date">
                          <IconCalendar size={11} />
                          {new Date(getPropertyValue(item, dateProp.id)).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                      {tagProp && Array.isArray(getPropertyValue(item, tagProp.id)) && getPropertyValue(item, tagProp.id).slice(0, 2).map((t: any, i: number) => (
                        <span key={i} className="fc-list-card-tag">#{typeof t === 'string' ? t : t.name}</span>
                      ))}
                    </div>
                  </div>
                  {/* Trailing checkbox + overflow */}
                  <div className="fc-list-card-trailing">
                    <Checkbox
                      size="sm"
                      checked={isSelected}
                      onChange={(e) => { e.stopPropagation(); toggleSelect(item.id); }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Menu shadow="md" position="bottom-end" withArrow>
                      <Menu.Target>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          onClick={(e) => e.stopPropagation()}
                          aria-label="More actions"
                        >
                          <IconDots size={14} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => { setEditingItem(item); setIsEditModalOpen(true); }}>Edit</Menu.Item>
                        <Menu.Item leftSection={<IconCopy size={14} />} onClick={() => onItemDuplicate?.(item)}>Duplicate</Menu.Item>
                        <Menu.Divider />
                        <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => handleDeleteItem(item.id)}>Delete</Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </div>
                </Box>
              );
            })
          )}
        </Box>

        <EditItemModal
          opened={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          item={editingItem}
          onSave={(item) => { onItemUpdate?.(item); setIsEditModalOpen(false); }}
        />
      </div>
    );
  }

  // ─── DESKTOP: rich table ────────────────────────────────────────────
  return (
    <div className="list-view-wrapper" style={{ width: '100%' }}>
      {/* Bulk action bar */}
      {selectedItems.length > 0 && (
        <Group
          justify="space-between"
          align="center"
          px="md"
          py="xs"
          style={{
            backgroundColor: 'var(--fc-bg-secondary)',
            borderBottom: '1px solid var(--fc-border-primary)',
          }}
        >
          <Group gap="sm">
            <Text size="sm" fw={500}>{selectedItems.length} selected</Text>
            <Button size="xs" variant="subtle" onClick={() => setSelectedItems([])}>Clear</Button>
          </Group>
          <Group gap="xs">
            <Button size="xs" variant="light" color="red" leftSection={<IconTrash size={14} />} onClick={() => {
              if (confirm(`Delete ${selectedItems.length} items?`)) {
                selectedItems.forEach(id => onItemDelete?.(id));
                setSelectedItems([]);
              }
            }}>Delete</Button>
          </Group>
        </Group>
      )}

      <Group justify="space-between" px="md" py="xs" align="center">
        <Text size="xs" c="dimmed">{items.length} {items.length === 1 ? 'item' : 'items'}</Text>
        <Tooltip label={density === 'compact' ? 'Switch to comfortable' : 'Switch to compact'}>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => setDensity(d => d === 'compact' ? 'comfortable' : 'compact')}
            aria-label="Toggle density"
            title="Toggle density"
          >
            {density === 'compact' ? <IconLayoutList size={14} /> : <IconList size={14} />}
          </ActionIcon>
        </Tooltip>
      </Group>

      <div
        className="fc-list-table"
        style={{ ['--fc-list-col-count' as any]: displayProperties.length }}
      >
        {/* Table header */}
        <div className="fc-list-row fc-list-row-header">
          <div className="fc-list-cell fc-list-cell-check">
            <Checkbox
              size="sm"
              checked={selectedItems.length === items.length && items.length > 0}
              onChange={handleSelectAll}
            />
          </div>
          <div className="fc-list-cell fc-list-cell-cover" />
          <div className="fc-list-cell fc-list-cell-title">Title</div>
          {displayProperties.map((p: any) => (
            <div key={p.id} className="fc-list-cell fc-list-cell-prop">{p.name}</div>
          ))}
          <div className="fc-list-cell fc-list-cell-actions" />
        </div>

        {items.length === 0 ? (
          <Box py="xl" ta="center">
            <Text c="dimmed">No items in this view</Text>
          </Box>
        ) : (
          items.map(item => {
            const isSelected = selectedItems.includes(item.id);
            return (
              <div
                key={item.id}
                className={`fc-list-row ${isSelected ? 'fc-list-row-selected' : ''} ${density === 'compact' ? 'fc-list-row--compact' : ''}`}
                onClick={() => onItemSelect?.(item)}
              >
                <div className="fc-list-cell fc-list-cell-check">
                  <Checkbox
                    size="sm"
                    checked={isSelected}
                    onChange={() => toggleSelect(item.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="fc-list-cell fc-list-cell-cover">
                  <div className="fc-list-row-thumb" style={{ background: getCover(item, database) }} />
                </div>
                <div className="fc-list-cell fc-list-cell-title">
                  <Text size="sm" fw={500} lineClamp={1}>{item.title}</Text>
                </div>
                {displayProperties.map((p: any) => (
                  <div key={p.id} className="fc-list-cell fc-list-cell-prop">
                    {p.type === 'status' || p.type === 'select' || p.id === 'status' || p.id === 'priority' || p.id === 'assignee'
                      ? renderStatusPill(getPropertyValue(item, p.id), p)
                      : p.type === 'date' || p.id === 'date'
                      ? <Text size="sm" c="dimmed">{getPropertyValue(item, p.id) !== '-' ? new Date(getPropertyValue(item, p.id)).toLocaleDateString() : '—'}</Text>
                      : <Text size="sm" lineClamp={1}>{getPropertyValue(item, p.id)}</Text>}
                  </div>
                ))}
                <div className="fc-list-cell fc-list-cell-actions">
                  <Menu shadow="md" position="bottom-end" withArrow>
                    <Menu.Target>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={(e) => e.stopPropagation()}
                        aria-label="More actions"
                      >
                        <IconDots size={14} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => { setEditingItem(item); setIsEditModalOpen(true); }}>Edit</Menu.Item>
                      <Menu.Item leftSection={<IconCopy size={14} />} onClick={() => onItemDuplicate?.(item)}>Duplicate</Menu.Item>
                      <Menu.Divider />
                      <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => handleDeleteItem(item.id)}>Delete</Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </div>
              </div>
            );
          })
        )}
      </div>

      <EditItemModal
        opened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        item={editingItem}
        onSave={(item) => { onItemUpdate?.(item); setIsEditModalOpen(false); }}
      />
    </div>
  );
};

const EditItemModal: React.FC<{
  opened: boolean;
  onClose: () => void;
  item: DatabaseItem | null;
  onSave: (item: DatabaseItem) => void;
}> = ({ opened, onClose, item, onSave }) => {
  const [title, setTitle] = useState('');
  useEffect(() => { if (item) setTitle(item.title); }, [item]);
  return (
    <Modal opened={opened} onClose={onClose} title="Edit Item" size="md">
      {item && (
        <Stack>
          <TextInput
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            autoFocus
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={onClose}>Cancel</Button>
            <Button onClick={() => onSave({ ...item, title })}>Save</Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};

export default ListView;
